import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { Loader2Icon } from 'lucide-react';
import notifyHB from '@/config/notifyHB';
import { toast } from '@/components/toast/toast';
import ComponentContainer from '@/components/component-container/ComponentContainer';
import { AuthContext } from '@/components/auth-context/auth-context';
import routeConfig from '@/config/routeConfig';
import OtpConfirmation, {
	OtpConfirmationProps,
} from '@/pages/login/otpConfirmation';
import { signup } from '@/use-cases/signup';
import { login } from '@/use-cases/login';
import { getUserCertificates } from '@/use-cases/get-user-signatures';
import { getErrorReason, trackEvent } from '@/utils/analytics';
import logo from '@/images/logo_cybersign--black.svg';
import styles from './solicitar-styles.module.css';

const REQUIRED_LOGIN_FIELDS = ['session', 'method', 'email', 'user_id'];
const hasRequiredFields = (data: Record<string, unknown>): boolean =>
	REQUIRED_LOGIN_FIELDS.every((field) => !!data?.[field]);

const Solicitar: React.FC = () => {
	const [isLoading, setIsLoading] = useState(false);
	const [email, setEmail] = useState('');
	const [sessionId, setSessionId] = useState('');
	const [userId, setUserId] = useState('');
	const [showOtpForm, setShowOtpForm] = useState(false);
	const history = useHistory();
	const authCtx = useContext(AuthContext);

	useEffect(() => {
		document.body.classList.add('solicitar-page');
		return () => document.body.classList.remove('solicitar-page');
	}, []);

	const handleStart = async (event: React.FormEvent) => {
		event.preventDefault();

		let failedCall = 'signup';
		try {
			setIsLoading(true);
			const signupResponse = await signup(email);

			if (signupResponse.status === 200 || signupResponse.status === 201) {
				failedCall = 'login';
				const loginResponse = await login(email);

				if (hasRequiredFields(loginResponse)) {
					setSessionId(loginResponse.session);
					setUserId(loginResponse.user_id);
					localStorage.setItem('user-email', loginResponse.email);
					localStorage.setItem('user-id', loginResponse.user_id);
					setShowOtpForm(true);
				} else {
					toast({ mode: 'destructive', description: 'Error al iniciar sesión' });
					trackEvent('onboarding_error', {
						step: 'login',
						reason: 'login_failed',
					});
				}
			} else {
				toast({ mode: 'destructive', description: 'El registro no fue exitoso.' });
				trackEvent('onboarding_error', {
					step: 'login',
					reason: 'signup_failed',
				});
			}
		} catch (error) {
			toast({
				mode: 'destructive',
				description: 'Ocurrió un error inesperado. Intenta de nuevo más tarde',
			});
			trackEvent('onboarding_error', {
				step: 'login',
				reason: `${failedCall}_${getErrorReason(error)}`,
			});
			notifyHB('login', 'Solicitar', error);
		} finally {
			setIsLoading(false);
		}
	};

	const redirectSession = async (onboardingUlid: string) => {
		try {
			const certificates = await getUserCertificates();
			if (certificates.length === 0) {
				// The wizard reads the step from the onboarding status endpoint.
				if (onboardingUlid) {
					localStorage.setItem('onboarding-ulid', onboardingUlid);
				}
				history.push(routeConfig.requestSignature);
			} else {
				history.push(routeConfig.dashboard);
			}
		} catch (error) {
			history.push(routeConfig.dashboard);
		}
	};

	const handleOtpSuccess: OtpConfirmationProps['onSuccess'] = async (
		loginData,
	) => {
		authCtx.saveToken(loginData);
		await redirectSession(loginData.onboarding_process_ulid);
	};

	return (
		<ComponentContainer customBackground>
			<div className={styles.page}>
			{showOtpForm ? (
				<div className={styles['otp-wrapper']}>
					<OtpConfirmation
						email={email}
						sessionId={sessionId}
						userId={userId}
						onSuccess={handleOtpSuccess}
						onCancel={() => setShowOtpForm(false)}
					/>
				</div>
			) : (
				<div className={styles.card}>
					<div className={styles.header}>
						<img className={styles.logo} src={logo} alt="Cybersign" />
						<button
							type="button"
							className={styles['login-link']}
							onClick={() => history.push(routeConfig.login)}
						>
							<span className={styles['desktop-only']}>
								¿Ya tienes cuenta?{' '}
							</span>
							Iniciar sesión
						</button>
					</div>

					<div className={styles.content}>
						<div className={styles.intro}>
							<p className={styles.eyebrow}>
								FIRMA ELECTRÓNICA AVANZADA · GUATEMALA
							</p>
							<h1 className={styles.headline}>
								Solicita tu firma electrónica en
								<br className={styles['desktop-only']} />{' '}
								10 minutos
							</h1>
							<p className={styles.subtitle}>
								Solo necesitas tu DPI y tu celular. Todo el proceso es en
								línea, sin visitar oficinas.
							</p>
						</div>

						<div className={styles.steps}>
							<div className={styles.step}>
								<span className={styles['step-number']}>1</span>
								<div className={styles['step-body']}>
									<h2 className={styles['step-title']}>
										Correo, datos y verificación
									</h2>
									<p className={styles['step-text']}>
										Código a tu correo, luego tus datos y confirmación por SMS.
									</p>
								</div>
							</div>
							<div className={styles.step}>
								<span className={styles['step-number']}>2</span>
								<div className={styles['step-body']}>
									<h2 className={styles['step-title']}>Elige tu plan y paga</h2>
									<div className={styles.plans}>
										<span className={styles.plan}>
											<span className={styles['plan-price']}>Q200</span> · 12
											meses
										</span>
										<span className={styles.plan}>
											<span className={styles['plan-price']}>Q110</span> · 1 mes
										</span>
									</div>
									<p className={styles['step-text']}>
										Tarjeta o transferencia
										<span className={styles['desktop-only']}> bancaria.</span>
										<span className={styles['mobile-only']}>
											. Renueva al mismo precio.
										</span>
									</p>
								</div>
							</div>
							<div className={styles.step}>
								<span className={styles['step-number']}>3</span>
								<div className={styles['step-body']}>
									<h2 className={styles['step-title']}>
										Comprueba tu identidad y firma
									</h2>
									<p className={styles['step-text']}>
										Fotos de tu DPI y una selfie
										<span className={styles['desktop-only']}> desde tu celular</span>
										. Tu firma queda lista para usarse desde web o app.
									</p>
								</div>
							</div>
						</div>

						<form className={styles['email-form']} onSubmit={handleStart}>
							<div className={styles['email-field']}>
								<label className={styles['email-label']} htmlFor="email">
									Correo electrónico
								</label>
								<input
									id="email"
									type="email"
									className={styles['email-input']}
									placeholder="tu@correo.com"
									value={email}
									required
									autoFocus
									disabled={isLoading}
									onChange={(e) => setEmail(e.target.value)}
								/>
							</div>
							<button
								type="submit"
								className={styles.submit}
								disabled={isLoading}
							>
								{isLoading && <Loader2Icon className="animate-spin" />}
								Comencemos
							</button>
						</form>
					</div>

					<div className={styles['legal-mobile']}>
						<p>
							<strong>Validez legal.</strong> Cybersign es Prestador de
							Servicios de Certificación (PSC) autorizado.
						</p>
						<p>Resolución RPSC-PSC-CYBERSIGN-006-2023.</p>
						<p>Tu firma tiene validez legal según el Decreto 47-2008.</p>
						<p className={styles['legal-mobile-gap']}>
							Reconocimiento internacional. Guatemala mantiene acuerdos de
							reconocimiento mutuo de firma electrónica con Costa Rica,
							República Dominicana y Panamá.
						</p>
						<a
							className={styles['legal-link']}
							href="https://www.rpsc.gob.gt/paises"
							target="_blank"
							rel="noopener noreferrer"
						>
							Ver acuerdos de reconocimiento
						</a>
					</div>

					<div className={`${styles.legal} ${styles['legal-desktop']}`}>
						<div>
							<p>
								<strong className={styles['legal-strong-dark']}>
									Validez legal.
								</strong>{' '}
								Cybersign es Prestador de Servicios de Certificación (PSC)
								autorizado.
							</p>
							<p>Resolución RPSC-PSC-CYBERSIGN-006-2023.</p>
							<p>Tu firma tiene validez legal según el Decreto 47-2008.</p>
						</div>
						<p>
							<strong>Reconocimiento internacional.</strong> Guatemala mantiene
							acuerdos de reconocimiento mutuo de firma electrónica con Costa
							Rica, República Dominicana y Panamá.{' '}
							<a
								className={styles['legal-link']}
								href="https://www.rpsc.gob.gt/paises"
								target="_blank"
								rel="noopener noreferrer"
							>
								Ver acuerdos de reconocimiento
							</a>
						</p>
					</div>
				</div>
			)}
			</div>
		</ComponentContainer>
	);
};

export default Solicitar;
